import { InputText } from "primereact/inputtext";
import {
  FieldDefinition,
  PipelineState,
  PortDefinition,
  TabDefinition,
} from '../../editorSlice';
import { useGraph } from "../../../../hooks/useGraph";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import "./NodeConfiguration.scss";
import { DataNodeModel, NodeKind } from "../../diagram/nodes/DataNodeModel";
import _ from "lodash";
import { TabPanel, TabView } from "primereact/tabview";
import { Chip } from "primereact/chip";
import { useNodeForm } from "../../../../hooks/useNodeForm";
import ReactMarkdown from "react-markdown";
import { Button } from "primereact/button";
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from "react-i18next";
import { setDialog, setToast } from '../../../../app/appSlice';
import remarkGfm from "remark-gfm";
import rehypeMermaid from "./rehype-mermaid";
import rehypeRaw from "rehype-raw";
import mermaid from "mermaid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import MdEditor from 'react-markdown-editor-lite';
// import style manually
import "react-markdown-editor-lite/lib/index.css";
import { Dialog } from "primereact/dialog";
import {
  useLazyGetCatalogQuery,
  usePostNewNodeMutation,
} from "../../../libraries/DataLibrarySlice";
import { setChangedTopology } from "../../editorSlice";
import CheckConditionValue from "../../../../utils/checkConditionValue";
import { AutoComplete } from 'primereact/autocomplete';
import { InputTextarea } from 'primereact/inputtextarea';
import { RuntimeMode } from '../../../../app/apiSlice';
import { DiagramNodeTag } from "../../../../components/DiagramNodeTag/DiagramNodeTag";
import SubProcessToolbar from "./SubProcessToolbar";

export enum NodeRunningState {
  UNDEFINED,
  PAUSED,
  STOPPING,
  RESUMING,
  RUNNING,
}

export const NodeConfiguration = () => {
  const { t } = useTranslation();
  const { activeNode, engine, render, getNodesByKind } = useGraph();
  const { getForm } = useNodeForm();
  const [data, setData] = useState<DataNodeModel | undefined>();
  const [activeIndex, setActiveIndex] = useState(1);
  const [getCatalog,{data:catalog,...statusCatalog}] = useLazyGetCatalogQuery();
  const [editDescription, setEditDescription] = useState(false);
  const [description, setDescription] = useState("");
  const dispatch = useAppDispatch();
  const [confirmDiag, setConfirmDiag] = useState<boolean>(false);
  const [postNode, status] = usePostNewNodeMutation();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isErrorDialog, setIsErrorDialog] = useState<boolean>(false);
  const topology = useAppSelector((state) =>  state.editor.current);
  const pipelineState = useAppSelector((state) =>  state.editor.pipelineState);
  const [itemsInference, setItemsInference] = useState<string[]>([]);
  const [inferenceCheckedList, setInferenceCheckedList] = useState<string[]>([]);
  const [suggestionsInference, setSuggestionsInference] = useState<string[]>([]);
  const SETTINGS_INFERENCE = 'inferenceList';
  const [runningState, setRunningState] = useState<NodeRunningState>(NodeRunningState.RUNNING);

  useEffect(() => {
      if (topology && !catalog) {
        getCatalog({runtimeType:topology.runtimeMode},true)
      }
  },[topology]);

  useEffect(() => {
    if (status.isSuccess) {
      dispatch(
        setToast({
          life: 3000,
          content: `Brique copiée !`,
          title: "Ajout de brique",
          severity: "success",
        })
      );
    }
    if (status.isError) {
      dispatch(
        setToast({
          life: 3000,
          content: `Erreur dans la copie de la brique!`,
          title: "Erreur",
          severity: "error",
        })
      );
    }
  }, [status]);

  useEffect(() => {
    if (activeNode && catalog) {
      setItemsInference([]);

      if (activeNode.settings) {
        setInferenceCheckedList(activeNode?.settings[SETTINGS_INFERENCE] ?? []);
        const dagItems = render({
          hidePassword: false,
          catalog: catalog
        });

        if (dagItems) {
          dagItems.map(item => {
            if (item['schema']) {
              try {
                const data = JSON.parse(item['schema']);
                const items = data.fields && data.fields.map((f: {name:string}) => '$.'+f.name);
                if (items) {
                  setItemsInference(prevState => prevState.concat(items.filter((i: string) => !prevState.includes(i))));
                }
              } catch (e) {
                console.log('Failed to parse Schema !');
              }
            }
          });
        }
      }
      setActiveIndex(0);

      if (activeNode.description) {
        setDescription(activeNode.description);
      } else {
        if (activeNode.schema && activeNode.schema.description) {
          setDescription(activeNode.schema.description);
        } else {
          setDescription("");
        }
      }
    }
  }, [activeNode, catalog]);

  // render Mermaid diagrams after <ReactMarkdown> has rendered the markdown content

  useEffect(() => {
      mermaid.initialize({ startOnLoad: true });
      mermaid.contentLoaded();
      }, [activeIndex]);  
    
  if (!activeNode) {
    return (
      <div style={{ padding: "10px", overflow: "hidden" }}>
        <h2>Node configuration</h2>
        <p>{t("message.select-node")}</p>
      </div>
    );
  }

  /* Avoid null and undefined value for default values */
  const filterDefaultValues = (defaultValue: any) => {
    if (defaultValue === undefined || defaultValue === null) return "";
    return defaultValue;
  };

  const isPipelineRunning = (): boolean => {
    return pipelineState !== PipelineState.STOPPED && pipelineState !== PipelineState.DELETED && pipelineState !== PipelineState.COMPLETED && pipelineState !== PipelineState.FAILED;
  }

  const isLastEnabledSource = (activeNode : DataNodeModel): boolean => {
    const listSource = getNodesByKind("source")
    for (const source of listSource) {
      if(source.getID() != activeNode.getID() && !source.isDisabled)
        return false
    }
    return true
  }
  const renderView = (
    key: string,
    fieldDefinition: FieldDefinition,
    value: any
  ) => {
    const Element = getForm(fieldDefinition?.type);
    return (
      <Element
        keyField={key}
        type={fieldDefinition?.type}
        typeParam={fieldDefinition?.type_param}
        defaultValue={filterDefaultValues(fieldDefinition?.default_value)}
        value={value}
        regex={fieldDefinition.regex ? new RegExp(fieldDefinition.regex) : undefined}
        regex_msg={fieldDefinition.regex_msg}
        placeholder={fieldDefinition?.placeholder}
        onChange={(value, valueWidget) =>
          setValue(
            key,
            value,
            valueWidget
          )
        }
        disabled={isPipelineRunning() && runningState !== NodeRunningState.PAUSED}
        onChangeAdvanced={(value) => {
          activeNode.settings!["advanced_mode"] = value;
          activeNode.fireEvent(
            {
              newSetting: { ["advanced_mode"]: value },
              isCreated: true,
            },
            "SettingsUpdated"
          );
        }}
        nodeId={activeNode.getID()}
      />
    );
  };

  const setValue = (
    key: string,
    value: any,
    valueWidget?: any
  ) => {
    // TODO create an hook to store value in graph and refresh graph (used for example for update node name)
    // Automagik! all is reference in JS ;) node is automatically updated without any dispatch/update
    // setNode() is only used to refresh component fields

    // (Using `==` and `!=` because sometimes `default_value` is a string and `value` is a number)

    if (valueWidget) {
      activeNode.settings![key] = value;
      activeNode.settings![key + "ValueWidget"] = valueWidget;
      activeNode.fireEvent(
        {
          newSetting: { [key]: value, [key + "ValueWidget"]: valueWidget },
          isCreated: true,
        },
        "SettingsUpdated"
      );
    } else {
      activeNode.settings![key] = value;
      activeNode.fireEvent(
        {
          newSetting: { [key]: value },
          isCreated: true,
        },
        "SettingsUpdated"
      );
    }
    update();
  };

  const update = () => {
    setData(_.clone(activeNode));
    engine?.repaintCanvas();
  };

  const handleEditorChange = (res: any) => {
    setDescription(res.text);
  };

  const saveDescription = () => {
    if (activeNode) {
      activeNode.description = description;
      update();
      setEditDescription(false);
      dispatch(setChangedTopology(true));
    }
  };

  const saveNewNode = () => {
    let node = activeNode.schema;
    let options = activeNode.getOptions();
    let settingsNode = activeNode.settings;
    if (node && settingsNode) {
      let errors: string[] = [];
      if (options.name === "") {
        errors.push(t("message.data.create.error.name"));
      }
      if (description === "") {
        errors.push(t("message.data.create.error.description"));
      }
      if (errors.length) {
        setErrorMessages(errors);
        setIsErrorDialog(true);
      } else {
        let sideout: any = [];
        if (node.sideout) {
          for (let [key, side] of Object.entries(node.sideout)) {
            if (side.schema) {
              side = {
                id: side.id,
                name: side.name,
                schema: side.schema
                  .replaceAll("\n\n", "\n")
                  .replaceAll("\n", "\n      "),
              };
              sideout.push(side);
            }
          }
        }

        let settings: any = [];
        for (let [key, setting] of Object.entries(node.settings)) {
          for (let [keyNode, settingNode] of Object.entries(settingsNode)) {
            if (key === keyNode) {
              if (settingNode) {
                setting = {
                  ...setting,
                  default_value: settingNode,
                };
              } else {
                setting = {
                  ...setting,
                  default_value: "",
                };
              }
            }
          }

          if (setting["help"]) {
            const help = setting["help"]
              .replaceAll("\n\n", "\n")
              .replaceAll("\n", "\n      ");
            if (
              setting["default_value"] &&
              typeof setting["default_value"] === "string"
            ) {
              const default_value = setting["default_value"]
                .replaceAll("\n\n", "\n")
                .replaceAll("\n", "\n      ");
              settings.push({
                ...setting,
                help: help,
                default_value: default_value,
                title: key,
              });
            } else {
              settings.push({
                ...setting,
                help: help,
                title: key,
              });
            }
          } else {
            settings.push({
              ...setting,
              title: key,
            });
          }
        }
        let tabs: any = [];
        node.tabs?.forEach((tab: any) => {
          const tabM: any = {
            name: tab.name,
            field: `[${tab.fields.toString()}]`,
          };
          tabs.push(tabM);
        });
        let nodeChanged: any = {
          ...node,
          sideout: sideout,
          name: options.name,
          description: node.description
            .replaceAll("\n\n", "\n")
            .replaceAll("\n", "\n      "),
          settings: settings,
          tabs: tabs,
          id_source_externe: -1,
        };
        postNode(nodeChanged);
      }
    }
  };

  const renderPort = (port: PortDefinition, index: number) => {
    return (
      <div className="col-12 p-3" key={"field-port" + port.name}>
        <div className="p-label">
          <b>
            <label>{port.name}</label>
          </b>
        </div>
        <div className="p-value">
          {port.fields
            ? port.fields.map((c, i) => (
                <Chip key={c + i} label={c} className={"m-2"} />
              ))
            : ""}
        </div>
      </div>
    );
  };

  const renderTabs = () => {
    let tabsArray: JSX.Element[] = [];

    if (activeNode.kind !== NodeKind.NOTE) {
      if (activeNode.schema?.tabs && activeNode.schema?.settings) {
        tabsArray = activeNode.schema.tabs.map((t: TabDefinition) => {
          let fields = {} as { [key: string]: FieldDefinition };
          t.fields.forEach((field) => {
            fields[field] = activeNode.schema!.settings[field];
          });
          return renderTab(t, fields, activeNode.schema!.settings);
        });
      }

      tabsArray.push(<TabPanel header={t("table.parameters")} key="parameters">
        <div className="grid">
          <div className="col-12 p-3">
            <div className="p-label">
              <b className={'flex justify-content-between align-items-center'}>
                <label htmlFor="name">{t('label.displayName')}</label>
              </b>
            </div>
            <div className="p-field">
              <InputText
                  autoFocus style={{width: '100%'}}
                  value={activeNode.getName()}
                  disabled={isPipelineRunning() && runningState !== NodeRunningState.PAUSED}
                  onChange={(e) => {
                    activeNode?.setName(e.target.value);
                    update();
                  }}
              />
            </div>
          </div>

          {activeNode.schema?.runtimeMode === RuntimeMode.BASIC && <div className="col-12 p-3">
            <div className="p-label">
              <b className={'flex justify-content-between align-items-center'}>
                <label htmlFor="name">{t('label.podName')}</label>
              </b>
            </div>
            <div className="p-field">
              <InputText
                  style={{width: '100%'}}
                  value={activeNode.getID()}
                  disabled={isPipelineRunning() && runningState !== NodeRunningState.PAUSED}
                  onChange={(e) => {
                    let value = e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, '')
                      .slice(0, 15);
                    activeNode?.setId(value);
                    update();
                  }}
              />
            </div>
          </div>}

          {activeNode.schema && activeNode.schema.input && activeNode.schema.input.map(renderPort)}
          {activeNode.schema && activeNode.schema.output && activeNode.schema.output.map(renderPort)}
          <div className="col-12 m-1 sizeNameNode">
            <div className="p-label">
              <b className="sizeNameNode">Type : </b>
            </div>
            <div className="p-value">
              {activeNode.schema?.name}
              <div className="icon">
                <img src={activeNode.schema?.icon} alt="" />
              </div>
            </div>
          </div>
        </div>
      </TabPanel>);

      tabsArray.push(<TabPanel header="Documentation" key="documentation">
        <div className="grid">
          <div className="col-12 p-field m-1">
            <div className="node-description p-4">
              {editDescription ? (
                  <>
                    <MdEditor
                        value={description}
                        style={{ height: "300px" }}
                        onChange={handleEditorChange}
                        renderHTML={(text: string) => {
                          return <ReactMarkdown 
		                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw, rehypeMermaid]}	  
			            children={text || ""} />;
                        }}
                    />
                    <Button
                        icon="pi pi-save"
                        label={t("button.save")}
                        onClick={() => saveDescription()}
                        className="m-2 p-button-outlined p-button-rounded p-button-sm"
                    />
                  </>
              ) : (
                  <>
                    <ReactMarkdown 
		            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw, rehypeMermaid]}  		    
		    	    children={description} />
                    <Button
                        icon="pi pi-pencil"
                        label={t("button.edit")}
                        disabled={isPipelineRunning() && runningState !== NodeRunningState.PAUSED}
                        onClick={() => {
                          setEditDescription(true);
                        }}
                        className="m-2 p-button-outlined p-button-rounded p-button-sm"
                    />
                  </>
              )}
            </div>
          </div>
        </div>
      </TabPanel>);
    } else {
      tabsArray.push(<TabPanel header={'Note'} key="note">
        <div className="grid">
          <div className="col-12 p-field m-1">
                <span className="p-label">
                  <b>
                    <label htmlFor="name">Descriptif : </label>
                  </b>
                  <br />
                  <InputTextarea
                      autoFocus={true} autoResize={false} spellCheck={false}
                      placeholder="Saisissez un commentaire"
                      rows={5} className="w-full"
                      value={activeNode?.getName() || ''}
                      onChange={(e) => {
                        activeNode.setName(e.target.value);
                        activeNode.fireEvent({ }, "nodesUpdated");
                        update();
                      }}
                  />
                </span>
          </div>
        </div>
      </TabPanel>);
    }
    return tabsArray;
  };

  const renderTab = (
    tabDef: TabDefinition,
    settings: { [key: string]: FieldDefinition },
    allSettings: { [key: string]: FieldDefinition }
  ) => {
    return (
      <TabPanel header={tabDef.name} key={"tab-" + tabDef.name}>
        <div className="settingsContainer">
          <div className="grid p-fluid">
            {!settings && (
              <div className="col-12 p-3">{t("label.no-parameters")}</div>
            )}
            {tabDef.description && <div className="flex align-items-center col-12 p-3">
              <h1 className="">{tabDef.description}</h1>
              {tabDef.advanced_description &&
                  <div style={{verticalAlign: 'middle', color: '#007eae'}}
                       onClick={() => {
                         dispatch(setDialog({
                           content: <div className="mb-3">
                             <ReactMarkdown 
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeRaw, rehypeMermaid]}
			          children={tabDef.advanced_description || ""} />
                           </div>,
                           title: "Documentation avancée",
                           width: "60%",
                         }))
                       }}
                  >
                    <span style={{ marginLeft: '1em' }}>
                      <i className="pi pi-info-circle"/>
                    </span>
                  </div>}
            </div>}
            {Object.keys(settings).map((key) => {
              // if the value condition exist : check if the value is wanted
              if (settings[key]?.condition) {
                if (
                  CheckConditionValue(
                    activeNode?.getSettings()?.[
                      allSettings[key].condition!.keyName
                    ],
                    settings[key].condition!.values,
                    allSettings[settings[key].condition!.keyName]?.default_value
                  )
                ) {
                  return renderSettings(key, settings[key]);
                }
                return;
              } else {
                return renderSettings(key, settings[key]);
              }
            })}
          </div>
        </div>
      </TabPanel>
    );
  };

  const updateInferenceList = (newInferenceList: string[]) => {
    setInferenceCheckedList(newInferenceList);
    setValue(SETTINGS_INFERENCE, newInferenceList);
  }

  const searchInference = (event: { query: string }) => {
    setTimeout(() => {
      let _filtered = [];
      if (!event.query.trim().length) {
        _filtered = [...itemsInference];
      } else {
        _filtered = itemsInference.filter(item => item.toLowerCase().includes(event.query.toLowerCase()));
      }
      setSuggestionsInference(_filtered);
    }, 250);
  }

  const renderSettings = (key: string, s: FieldDefinition) => {
    const isInference = inferenceCheckedList.includes(s.name);
    if (s?.hidden) {
      return <div key={key} />;
    }
    return (
      <div
        className="col-12 p-3"
        key={"field-" + activeNode?.getOptions().id + "-" + key + "-" + s?.type}
      >
        <div className="p-label">
          <b className={'flex justify-content-between align-items-center pb-1'}>
            <label htmlFor="name">
              {s?.name ? s.name : key} {s?.required && "*"}
            </label>
            {s?.inference && <Checkbox tooltip="Mode inférence" tooltipOptions={{ position: 'left' }}
              onChange={(e) => {
              if (e.checked as boolean) {
                updateInferenceList([...inferenceCheckedList, s.name]);
                setValue(key, undefined);
              } else if (inferenceCheckedList.includes(s.name)) {
                updateInferenceList(inferenceCheckedList.filter(e => e !== s.name));
                setValue(key, s.default_value);
              }
            }} checked={isInference}></Checkbox>}
          </b>{" "}
          {s?.help && <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw, rehypeMermaid]}  
			    children={s?.help} />}
        </div>
        <div className="p-field">
          {isInference ?
              <AutoComplete
                  value={activeNode!.getSettings()![key]}
                  suggestions={suggestionsInference}
                  completeMethod={searchInference}
                  onChange={(e) => {
                    setValue(key, e.target.value);
                  }}
                  //dropdown
              />
              : renderView(
            key,
            s,
            activeNode!.getSettings()![key]
          )}
        </div>
      </div>
    );
  };

  const renderErrorDialog = (errorMessages: string[]) => {
    return (
      <Dialog
        header={t("title.create-new-node-errors")}
        footer={
          <div>
            <Button
              label="OK"
              onClick={() => {
                setIsErrorDialog(false);
              }}
              className="p-button p-button-rounded p-button-sm p-button-danger"
            />
          </div>
        }
        visible={isErrorDialog}
        style={{ width: "30vw" }}
        modal
        onHide={() => setIsErrorDialog(false)}
      >
        <div className="row">
          {errorMessages.map((errorMessage) => {
            return (
              <>
                {errorMessage}
                <br />
              </>
            );
          })}
        </div>
      </Dialog>
    );
  };

  const isPauseAllowed = (): boolean => {
    if (activeNode.kind === NodeKind.NOTE || activeNode.kind === NodeKind.SOURCE) {
      return false;
    }
    if (topology) {
      const isCRON = topology.schedule !== undefined && topology.schedule != "";
      const isANALYTIC = topology.runtimeMode === RuntimeMode.ANALYTIC;
      return !isANALYTIC && !isCRON;
    }
    return false;
  }

  return (
    <div className="node-configuration">
      <div>
        <div className="top-center">
          {activeNode.isDeprecated && (
            <span className="deprecated-badge">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              {t("message.deprecated")}
            </span>
          )}
        </div>
        <div className="nodes-name">
          <div className="node-left">
            <label htmlFor="checkbox-disabled">Active</label>
            <Checkbox
              checked={!activeNode.isDisabled}
              onClick={(e) => {
                const isChecked = e.checked;
                if (!isChecked) {
                  if (!isLastEnabledSource(activeNode)) {
                    activeNode.setIsDisabled(true);
                  } else {
                    dispatch(setToast({severity: "info", content: t('error.desactivesource'), life: 5000}));
                  }
                } else {
                  activeNode.setIsDisabled(false);
                }
                activeNode.fireEvent({ }, "nodesUpdated");
                update();
              }}
            />
          </div>

          <div className={`node-center ${(activeNode.isDisabled ? 'node__disabled' : '')}`}>
            <DiagramNodeTag node={activeNode}/>
          </div>
          <div className="node-right">
            {isPauseAllowed() && pipelineState === PipelineState.RUNNING &&
            <SubProcessToolbar node={activeNode} onRunningStateChange={(state) => setRunningState(state)}/>
            }
          </div>
        </div>
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          {renderTabs()}
        </TabView>
      </div>
        <Dialog
          header={t("message.data.create.confirm.title")}
          footer={
            <div>
              <Button
                label="Oui"
                onClick={() => {
                  setConfirmDiag(false);
                  saveNewNode();
                }}
                className="p-button p-button-rounded p-button-sm"
              />
              <Button
                label="Non"
                onClick={() => {
                  setConfirmDiag(false);
                }}
                className="p-button p-button-rounded p-button-sm"
              />
            </div>
          }
          visible={confirmDiag}
          style={{ width: "30vw" }}
          modal
          onHide={() => {
            setConfirmDiag(false);
          }}
        >
          <div className="row">{t("message.data.create.confirm.message")}</div>
        </Dialog>
        {renderErrorDialog(errorMessages)}
      </div>
  );
};
